import React, { useState, useMemo } from "react";

export interface Shortcut {
  label: string;
  command: string;
  isLongRunning?: boolean;
}

interface ShortcutsBarProps {
  shortcuts: Shortcut[];
  isConnected: boolean;
  isDarkMode: boolean;
  isCommandRunning?: boolean;
  onExecute: (command: string, isLongRunning?: boolean) => void;
}

export const ShortcutsBar: React.FC<ShortcutsBarProps> = ({
  shortcuts,
  isConnected,
  isDarkMode,
  isCommandRunning = false,
  onExecute,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [runningCommandLabel, setRunningCommandLabel] = useState<string | null>(
    null,
  );

  const filteredShortcuts = useMemo(() => {
    let filtered = shortcuts;

    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = shortcuts.filter(
        (shortcut) =>
          shortcut.label.toLowerCase().includes(lowerSearchTerm) ||
          shortcut.command.toLowerCase().includes(lowerSearchTerm),
      );
    }

    return filtered;
  }, [shortcuts, searchTerm]);

  // The label only matters while a command runs; afterwards it is stale.
  const activeRunningCommandLabel = isCommandRunning
    ? runningCommandLabel
    : null;
  const containerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 20px",
    backgroundColor: isDarkMode ? "#1a1a1a" : "#f3f4f6",
    borderBottom: isDarkMode
      ? "1px solid rgba(255, 255, 255, 0.05)"
      : "1px solid rgba(0, 0, 0, 0.05)",
    flexShrink: 0,
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  };

  const labelStyle: React.CSSProperties = {
    color: isDarkMode ? "#6b7280" : "#9ca3af",
    fontSize: "11px",
    fontWeight: 500,
    marginRight: "8px",
    whiteSpace: "nowrap",
    flexShrink: 0,
  };

  const getButtonStyle = (isCtrlC = false): React.CSSProperties => ({
    padding: "4px 10px",
    backgroundColor:
      isCommandRunning && isCtrlC
        ? isDarkMode
          ? "rgba(239, 68, 68, 0.2)"
          : "rgba(220, 38, 38, 0.2)"
        : isDarkMode
          ? "rgba(255, 255, 255, 0.05)"
          : "rgba(0, 0, 0, 0.05)",
    border:
      isCommandRunning && isCtrlC
        ? isDarkMode
          ? "1px solid rgba(239, 68, 68, 0.5)"
          : "1px solid rgba(220, 38, 38, 0.5)"
        : isDarkMode
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
    borderRadius: "4px",
    color:
      isCommandRunning && isCtrlC
        ? isDarkMode
          ? "#ef4444"
          : "#dc2626"
        : isDarkMode
          ? "#d1d5db"
          : "#4b5563",
    fontSize: "11px",
    fontFamily: "monospace",
    fontWeight: isCommandRunning && isCtrlC ? 600 : 500,
    cursor: isConnected ? "pointer" : "not-allowed",
    opacity: isConnected ? 1 : 0.5,
    transition: "all 0.15s ease",
    whiteSpace: "nowrap",
    flexShrink: 0,
    minHeight: "28px",
    display: "flex",
    alignItems: "center",
    touchAction: "manipulation",
  });

  const getDisabledButtonStyle = (): React.CSSProperties => ({
    padding: "4px 10px",
    backgroundColor: isDarkMode
      ? "rgba(255, 255, 255, 0.02)"
      : "rgba(0, 0, 0, 0.02)",
    border: isDarkMode
      ? "1px solid rgba(255, 255, 255, 0.05)"
      : "1px solid rgba(0, 0, 0, 0.05)",
    borderRadius: "4px",
    color: isDarkMode ? "#6b7280" : "#9ca3af",
    fontSize: "11px",
    fontFamily: "monospace",
    fontWeight: 500,
    cursor: "not-allowed",
    opacity: 0.4,
    whiteSpace: "nowrap",
    flexShrink: 0,
    minHeight: "28px",
    display: "flex",
    alignItems: "center",
    touchAction: "manipulation",
  });

  const searchInputStyle: React.CSSProperties = {
    padding: "4px 10px",
    backgroundColor: isDarkMode
      ? "rgba(255, 255, 255, 0.05)"
      : "rgba(0, 0, 0, 0.05)",
    border: isDarkMode
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(0, 0, 0, 0.1)",
    borderRadius: "4px",
    color: isDarkMode ? "#d1d5db" : "#4b5563",
    fontSize: "11px",
    fontFamily: "monospace",
    fontWeight: 500,
    height: "28px",
    minWidth: "150px",
    flexShrink: 0,
    outline: "none",
    transition: "all 0.15s ease",
    boxSizing: "border-box",
  };

  const clearButtonStyle: React.CSSProperties = {
    marginLeft: "-24px",
    background: "none",
    border: "none",
    color: isDarkMode ? "#6b7280" : "#9ca3af",
    cursor: "pointer",
    fontSize: "14px",
    padding: "0 4px",
    zIndex: 1,
    display: searchTerm ? "block" : "none",
  };

  return (
    <>
      <div style={containerStyle} className="shortcuts-container">
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <input
            type="text"
            placeholder="Shortcuts suchen..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            style={searchInputStyle}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = isDarkMode
                ? "rgba(255, 255, 255, 0.2)"
                : "rgba(0, 0, 0, 0.2)";
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = isDarkMode
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)";
            }}
          />
          <button
            style={clearButtonStyle}
            onClick={() => {
              setSearchTerm("");
            }}
            title="Suche löschen"
          >
            ✕
          </button>
        </div>
        {filteredShortcuts.length === 0 ? (
          <span style={{ ...labelStyle, fontStyle: "italic" }}>
            Keine Shortcuts gefunden
          </span>
        ) : (
          filteredShortcuts.map((shortcut) => {
            // Replace running command button with "Befehl beenden"
            const isRunningCommand =
              activeRunningCommandLabel === shortcut.label;
            const displayShortcut = isRunningCommand
              ? { label: "Befehl beenden", command: "\x03" }
              : shortcut;

            return (
              <button
                key={`${shortcut.label}\u0000${shortcut.command}`}
                style={
                  !isConnected || (isCommandRunning && !isRunningCommand)
                    ? getDisabledButtonStyle()
                    : getButtonStyle(isRunningCommand)
                }
                onMouseEnter={(e) => {
                  if (
                    isConnected &&
                    (!isCommandRunning || isRunningCommand) &&
                    window.matchMedia("(hover: hover)").matches
                  ) {
                    e.currentTarget.style.backgroundColor = isDarkMode
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.1)";
                    e.currentTarget.style.transform = "translateY(-1px)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (isConnected && (!isCommandRunning || isRunningCommand)) {
                    e.currentTarget.style.backgroundColor = isRunningCommand
                      ? isDarkMode
                        ? "rgba(239, 68, 68, 0.2)"
                        : "rgba(220, 38, 38, 0.2)"
                      : isDarkMode
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.05)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }
                }}
                onClick={(e) => {
                  if (!isConnected || (isCommandRunning && !isRunningCommand)) {
                    return;
                  }

                  if (isRunningCommand) {
                    // Send Ctrl+C to stop the running command
                    onExecute("\x03");
                  } else {
                    // Track which button started the command
                    if (shortcut.isLongRunning) {
                      setRunningCommandLabel(shortcut.label);
                    }
                    onExecute(shortcut.command, shortcut.isLongRunning);
                  }

                  setSearchTerm("");
                  // Prevent button from keeping focus
                  e.currentTarget.blur();
                }}
                disabled={
                  !isConnected || (isCommandRunning && !isRunningCommand)
                }
                title={
                  isRunningCommand
                    ? "Befehl beenden (Ctrl+C)"
                    : `Execute: ${shortcut.command.trim()}`
                }
              >
                {displayShortcut.label}
              </button>
            );
          })
        )}
      </div>
      <style>
        {`
          .shortcuts-container::-webkit-scrollbar {
            display: none;
          }
          .shortcuts-container {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          
          @media (max-width: 767px) {
            .shortcuts-container {
              gap: 6px !important;
              padding: 6px 12px !important;
            }
            
            .shortcuts-container span {
              font-size: 10px !important;
              margin-right: 6px !important;
            }
            
            .shortcuts-container button {
              padding: 6px 10px !important;
              font-size: 10px !important;
              min-height: 32px !important;
            }
            
            .shortcuts-container input {
              font-size: 10px !important;
              min-width: 120px !important;
              padding: 6px 10px !important;
              height: 32px !important;
            }
          }
        `}
      </style>
    </>
  );
};
